import React, { useEffect, useState } from "react";
import { Check, ChevronDown, Minus, Puzzle, Receipt, ShieldCheck, Smartphone } from "lucide-react";
import api from "@/lib/api";

export const usePricing = () => {
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/pricing").then((r) => setP(r.data)).catch(() => {}); }, []);
  return p;
};

const Price = ({ v }) => (v ? <><b className="text-primary">{v}</b> <span className="text-[11px] text-muted-foreground">ريال</span></> : <b className="text-emerald-600 dark:text-emerald-400">مجانًا</b>);

/** جدول «خدمات حسب الطلب — بدون اشتراك» مع نطاق كل خدمة (قابل للطيّ) + البطاقة الرقمية كخدمة مستقلة */
export const ServicesTable = ({ pricing }) => {
  const [open, setOpen] = useState("");
  if (!pricing) return null;
  const list = pricing.services;
  return (
    <div className="rounded-2xl border border-border overflow-hidden" data-testid="services-table">
      <div className="grid grid-cols-[1fr_auto] text-[11px] text-muted-foreground bg-secondary/60 px-3 py-1.5"><span>الخدمة</span><span>السعر</span></div>
      {list.map((s) => {
        const isOpen = open === s.kind;
        return (
          <div key={s.kind} className="border-t border-border" data-testid={`service-row-${s.kind}`}>
            <button type="button" onClick={() => setOpen(isOpen ? "" : s.kind)} className="w-full grid grid-cols-[1fr_auto] items-center gap-2 px-3 py-2 text-start hover:bg-secondary/40" data-testid={`service-toggle-${s.kind}`}>
              <span className="text-sm flex items-center gap-1.5 min-w-0"><ChevronDown className={`w-3.5 h-3.5 shrink-0 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`} /><span className="truncate">{s.label}</span></span>
              <span className="text-sm whitespace-nowrap"><Price v={s.price} /></span>
            </button>
            {isOpen && (
              <ul className="px-4 pb-3 space-y-1 text-xs text-muted-foreground" data-testid={`service-scope-${s.kind}`}>
                <li className="font-bold text-foreground text-[11px]">نطاق الخدمة — ما يشمله الشراء:</li>
                {(s.scope || []).map((x, i) => <li key={i} className="flex gap-1.5"><Check className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />{x}</li>)}
              </ul>
            )}
          </div>
        );
      })}
      {pricing.digital_card && (
        <div className="border-t border-dashed border-accent/50 bg-accent/5 px-3 py-2.5" data-testid="service-row-digital-card">
          <div className="grid grid-cols-[1fr_auto] items-center gap-2">
            <span className="text-sm flex items-center gap-1.5"><Smartphone className="w-4 h-4 text-accent shrink-0" />{pricing.digital_card.label} <span className="text-[10px] rounded-full bg-accent/15 text-accent px-1.5 py-px">خدمة مستقلة</span></span>
            <span className="text-sm"><Price v={pricing.digital_card.price} /></span>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">{pricing.digital_card.note}</p>
        </div>
      )}
    </div>
  );
};

/** مصفوفة: أي باقة تشمل أي ميزة، وما يحتاج إضافة */
export const FeatureMatrix = ({ pricing }) => {
  if (!pricing) return null;
  const plans = pricing.plans.filter((p) => p.price > 0);
  const cell = (v) => v === true ? <Check className="w-4 h-4 text-primary mx-auto" /> : v === "addon" ? <span className="inline-flex items-center gap-1 text-[10px] rounded-full bg-accent/15 text-accent px-1.5 py-px"><Puzzle className="w-3 h-3" /> إضافة</span> : v ? <span className="text-[11px] font-bold">{v}</span> : <Minus className="w-4 h-4 text-muted-foreground/50 mx-auto" />;
  return (
    <div className="rounded-2xl border border-border overflow-x-auto" data-testid="feature-matrix">
      <table className="w-full text-xs min-w-[520px]">
        <thead className="bg-secondary/60 text-muted-foreground"><tr><th className="text-start px-3 py-2 font-medium">الميزة</th>{plans.map((p) => <th key={p.id} className="px-2 py-2 font-bold text-foreground whitespace-nowrap">{p.name}<span className="block text-[10px] font-normal text-muted-foreground">{p.price} ريال/شهر</span></th>)}</tr></thead>
        <tbody>
          <tr className="border-t border-border"><td className="px-3 py-2">الصور / الفيديوهات شهريًا</td>{plans.map((p) => <td key={p.id} className="px-2 py-2 text-center font-bold">{p.limits.images} / {p.limits.videos}</td>)}</tr>
          {pricing.feature_matrix.map((f) => (
            <tr key={f.key} className="border-t border-border" data-testid={`matrix-row-${f.key}`}><td className="px-3 py-2">{f.label}</td>{plans.map((p) => <td key={p.id} className="px-2 py-2 text-center">{cell((p.includes || {})[f.key])}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** سياسة الحصص والأرصدة + المعالجة الضريبية — تُعرض قبل الدفع */
export const QuotaPolicy = ({ pricing, only }) => {
  if (!pricing) return null;
  const keys = only || ["subscription", "credits", "wallet", "refund"];
  return (
    <div className="rounded-2xl border border-border bg-secondary/30 p-4 space-y-2 text-xs" data-testid="quota-policy">
      <div className="font-bold text-sm flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-primary" /> سياسة الحصص والأرصدة</div>
      {keys.map((k) => pricing.quota_policy[k] && <p key={k} className="text-muted-foreground leading-relaxed flex gap-1.5" data-testid={`policy-${k}`}><Check className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />{pricing.quota_policy[k]}</p>)}
      <p className="text-muted-foreground leading-relaxed flex gap-1.5 border-t border-border pt-2" data-testid="tax-note"><Receipt className="w-3.5 h-3.5 text-accent shrink-0 mt-0.5" />{pricing.tax_note}</p>
    </div>
  );
};
